<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

// Create / edit a company (name, domain, description) and, when editing,
// delete it behind a confirmation. Owns the store calls and emits `saved`
// / `deleted` so the list can refresh its counters.
const emit = defineEmits(['saved', 'deleted']);

const { t } = useI18n();
const companiesStore = useCompaniesStore();

const dialogRef = ref(null);
const deleteDialogRef = ref(null);
const isSaving = ref(false);
const isDeleting = ref(false);

const state = reactive({
  id: null,
  name: '',
  domain: '',
  description: '',
});

const rules = {
  name: { required, maxLength: maxLength(255) },
  description: { maxLength: maxLength(1000) },
};
const v$ = useVuelidate(rules, state);

const isEdit = computed(() => !!state.id);
const title = computed(() =>
  isEdit.value
    ? t('COMPANIES.DIALOG.EDIT_TITLE')
    : t('COMPANIES.DIALOG.CREATE_TITLE')
);
const confirmLabel = computed(() =>
  isEdit.value ? t('COMPANIES.DIALOG.SAVE') : t('COMPANIES.DIALOG.CREATE')
);

const open = (company = null) => {
  Object.assign(state, {
    id: company?.id ?? null,
    name: company?.name ?? '',
    domain: company?.domain ?? '',
    description: company?.description ?? '',
  });
  v$.value.$reset();
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();

const onConfirm = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;
  isSaving.value = true;
  const payload = {
    name: state.name.trim(),
    domain: state.domain.trim(),
    description: state.description.trim(),
  };
  try {
    if (isEdit.value) {
      await companiesStore.update({ id: state.id, ...payload });
      useAlert(t('COMPANIES.DIALOG.API.UPDATE_SUCCESS'));
    } else {
      await companiesStore.create(payload);
      useAlert(t('COMPANIES.DIALOG.API.CREATE_SUCCESS'));
    }
    emit('saved');
    close();
  } catch (error) {
    useAlert(error?.message || t('COMPANIES.DIALOG.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openDeleteConfirm = () => deleteDialogRef.value?.open();

const onDelete = async () => {
  isDeleting.value = true;
  try {
    await companiesStore.delete(state.id);
    useAlert(t('COMPANIES.DIALOG.API.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
    close();
    emit('deleted');
  } catch (error) {
    useAlert(error?.message || t('COMPANIES.DIALOG.API.ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :confirm-button-label="confirmLabel"
    :is-loading="isSaving"
    :disable-confirm-button="isSaving"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="t('COMPANIES.DIALOG.NAME_LABEL')"
        :placeholder="t('COMPANIES.DIALOG.NAME_PLACEHOLDER')"
        :message="v$.name.$error ? t('COMPANIES.DIALOG.NAME_ERROR') : ''"
        :message-type="v$.name.$error ? 'error' : 'info'"
        @blur="v$.name.$touch()"
      />
      <Input
        v-model="state.domain"
        :label="t('COMPANIES.DIALOG.DOMAIN_LABEL')"
        :placeholder="t('COMPANIES.DIALOG.DOMAIN_PLACEHOLDER')"
      />
      <TextArea
        v-model="state.description"
        :label="t('COMPANIES.DIALOG.DESCRIPTION_LABEL')"
        :placeholder="t('COMPANIES.DIALOG.DESCRIPTION_PLACEHOLDER')"
        :max-length="1000"
        show-character-count
        auto-height
      />
      <Policy v-if="isEdit" :permissions="['administrator']">
        <div class="flex justify-start -mb-1">
          <Button
            type="button"
            :label="t('COMPANIES.DIALOG.DELETE')"
            icon="i-lucide-trash"
            color="ruby"
            variant="link"
            size="sm"
            @click="openDeleteConfirm"
          />
        </div>
      </Policy>
    </div>
  </Dialog>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('COMPANIES.DIALOG.DELETE_CONFIRM.TITLE')"
    :description="t('COMPANIES.DIALOG.DELETE_CONFIRM.DESCRIPTION')"
    :confirm-button-label="t('COMPANIES.DIALOG.DELETE_CONFIRM.CONFIRM')"
    :is-loading="isDeleting"
    :disable-confirm-button="isDeleting"
    @confirm="onDelete"
  />
</template>
